import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { apiUrl } from './api';

let firebaseApp = null;
let firestoreDb = null;
let firebaseAuth = null;
let initPromise = null;

async function resolveFirebaseConfig() {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
  const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;

  if (projectId && apiKey) {
    return {
      projectId,
      apiKey,
      authDomain: authDomain || `${projectId}.firebaseapp.com`,
    };
  }

  const response = await fetch(apiUrl('/config/client'));
  if (!response.ok) {
    throw new Error('Unable to load Firebase configuration.');
  }

  const payload = await response.json();
  if (!payload.firebase?.projectId || !payload.firebase?.apiKey) {
    throw new Error('Firebase configuration is incomplete.');
  }

  return {
    projectId: payload.firebase.projectId,
    apiKey: payload.firebase.apiKey,
    authDomain: payload.firebase.authDomain || `${payload.firebase.projectId}.firebaseapp.com`,
  };
}

async function ensureFirebaseApp() {
  if (firebaseApp) {
    return firebaseApp;
  }

  if (!initPromise) {
    initPromise = (async () => {
      const config = await resolveFirebaseConfig();
      firebaseApp = getApps().length ? getApps()[0] : initializeApp(config);
      firestoreDb = getFirestore(firebaseApp);
      firebaseAuth = getAuth(firebaseApp);
      return firebaseApp;
    })();
  }

  await initPromise;
  return firebaseApp;
}

export async function getFirestoreDb() {
  await ensureFirebaseApp();
  return firestoreDb;
}

export async function getFirebaseAuth() {
  await ensureFirebaseApp();
  return firebaseAuth;
}

export async function signInWithEmail(email, password) {
  const auth = await getFirebaseAuth();
  return signInWithEmailAndPassword(auth, email.trim(), password);
}

export async function registerWithEmail(email, password) {
  const auth = await getFirebaseAuth();
  return createUserWithEmailAndPassword(auth, email.trim(), password);
}

export async function signInWithGoogle() {
  const auth = await getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

export async function signOutUser() {
  const auth = await getFirebaseAuth();
  return firebaseSignOut(auth);
}

export async function subscribeToAuthChanges(callback) {
  const auth = await getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

export async function getCurrentUserToken(forceRefresh = false) {
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}
